import { FC, ReactElement, useMemo, useCallback } from 'react';
import useBoxes from 'networking/mindfeed/hooks/useBoxes';
import { Box } from 'models/Box';
import { AccessLevel } from 'models/AccessLevel';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import MemoryBox from './MemoryBox';
import { removeLeadingNonAlphanumeric } from 'utils/StringUtils';
import SectionLabel from 'elements/SectionLabel';
import { useTranslations } from 'next-intl';

interface Props {
  onBoxSelect?: () => void;
  onBoxEdit?: () => void;
}

const MemoryBoxList: FC<Props> = ({ onBoxSelect, onBoxEdit }): ReactElement => {
  const t = useTranslations();
  const { selectedSpace, selectedBox } = useActiveSpace();
  const { data: boxes } = useBoxes();

  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }),
    [],
  );

  const compareNaturally = useCallback(
    (a: Box, b: Box) => {
      const nameA = removeLeadingNonAlphanumeric(a.name).trim();
      const nameB = removeLeadingNonAlphanumeric(b.name).trim();
      return collator.compare(nameA, nameB);
    },
    [collator],
  );

  // Private boxes are owned by the user
  const privateBoxes = useMemo(() => {
    if (!boxes) return [];
    return [...boxes]
      .filter((b) => b.accessLevel === AccessLevel.Private)
      .sort((a, b) => {
        if (a.isDefault) return -1;
        if (b.isDefault) return 1;
        return compareNaturally(a, b);
      });
  }, [boxes, compareNaturally]);

  // Shared boxes come from the API
  const sharedBoxes = useMemo(() => {
    return (boxes ?? [])
      .filter((b) => b.accessLevel === AccessLevel.Shared)
      .sort(compareNaturally);
  }, [boxes, compareNaturally]);

  const renderBox = (box: Box) => (
    <MemoryBox
      key={box.id}
      space={selectedSpace}
      box={box}
      isSelected={selectedBox?.id === box.id}
      onSelect={onBoxSelect}
      onEdit={onBoxEdit}
    />
  );

  return (
    <div className={styles.container}>
      {privateBoxes.length > 0 && (
        <>
          <SectionLabel className={styles.label}>
            {t('boxes.sections.private')}
          </SectionLabel>
          {privateBoxes.map(renderBox)}
        </>
      )}

      {sharedBoxes.length > 0 && (
        <>
          <SectionLabel className={styles.label}>
            {t('boxes.sections.shared')}
          </SectionLabel>
          {sharedBoxes.map(renderBox)}
        </>
      )}
    </div>
  );
};

const styles = {
  container: 'space-y-1',
  label: 'px-3 pt-2 pb-1 first:pt-0',
};

export default MemoryBoxList;
